/**
 * Payment Link Generator & Tracker
 *
 * Used by sales reps and admins to create Stripe checkout links
 * and track their lifecycle (awaiting_payment → paid → contract_created → signup_complete).
 *
 * Package presets:
 *   1. Monthly Subscription (default) — 30-day recurring, standard CPL
 *   2. Biweekly Starter — 14-day recurring, higher CPL, upsell funnel entry
 *   3. Prepaid Package — one-time payment, custom duration
 */

import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  CheckCircle2,
  Copy,
  CreditCard,
  Link as LinkIcon,
  Loader2,
  RefreshCw,
  Calendar,
  Zap,
  Package,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const LEAD_TYPES_BY_VERTICAL: Record<string, Array<{ id: string; name: string }>> = {
  life_insurance: [
    { id: "life_fex", name: "Final Expense (FEX)" },
    { id: "life_iul", name: "Indexed Universal Life (IUL)" },
    { id: "life_mp", name: "Mortgage Protection (MP)" },
  ],
  mortgage: [
    { id: "mortgage_purchase", name: "Purchase" },
    { id: "mortgage_refi", name: "Refinance" },
    { id: "mortgage_heloc", name: "HELOC" },
    { id: "mortgage_reverse", name: "Reverse Mortgage" },
  ],
};

type PackagePreset = "monthly" | "biweekly" | "prepaid";

interface PresetConfig {
  label: string;
  description: string;
  icon: React.ReactNode;
  contractType: string;
  cycleDays: number;
  billingLabel: string;
}

// Default leads & price per lead type per package preset.
// These are just initial values — admin can override everything before generating.
const LEAD_TYPE_DEFAULTS: Record<PackagePreset, Record<string, { leads: number; price: number }>> = {
  monthly: {
    life_iul:            { leads: 40, price: 55 },
    life_fex:            { leads: 50, price: 35 },
    life_mp:             { leads: 40, price: 45 },
    mortgage_purchase:   { leads: 40, price: 55 },
    mortgage_refi:       { leads: 40, price: 45 },
    mortgage_heloc:      { leads: 40, price: 45 },
    mortgage_reverse:    { leads: 40, price: 50 },
  },
  biweekly: {
    life_iul:            { leads: 15, price: 70 },
    life_fex:            { leads: 20, price: 50 },
    life_mp:             { leads: 15, price: 60 },
    mortgage_purchase:   { leads: 15, price: 70 },
    mortgage_refi:       { leads: 15, price: 60 },
    mortgage_heloc:      { leads: 15, price: 60 },
    mortgage_reverse:    { leads: 15, price: 65 },
  },
  prepaid: {
    life_iul:            { leads: 50, price: 55 },
    life_fex:            { leads: 50, price: 35 },
    life_mp:             { leads: 50, price: 45 },
    mortgage_purchase:   { leads: 50, price: 55 },
    mortgage_refi:       { leads: 50, price: 45 },
    mortgage_heloc:      { leads: 50, price: 45 },
    mortgage_reverse:    { leads: 50, price: 50 },
  },
};

// Fallback defaults when no lead type is selected yet
const PRESET_FALLBACK_DEFAULTS: Record<PackagePreset, { leads: number; price: number }> = {
  monthly:  { leads: 40, price: 55 },
  biweekly: { leads: 15, price: 70 },
  prepaid:  { leads: 50, price: 55 },
};

const PRESETS: Record<PackagePreset, PresetConfig> = {
  monthly: {
    label: "Monthly Subscription",
    description: "Standard recurring plan — best value per lead",
    icon: <Calendar className="h-4 w-4" />,
    contractType: "monthly_target",
    cycleDays: 30,
    billingLabel: "Billed every 30 days (auto-renews)",
  },
  biweekly: {
    label: "Biweekly Starter",
    description: "2-week intro plan — lower commitment, upsell to monthly",
    icon: <Zap className="h-4 w-4" />,
    contractType: "monthly_target",
    cycleDays: 14,
    billingLabel: "Billed every 14 days (auto-renews)",
  },
  prepaid: {
    label: "Prepaid Package",
    description: "One-time payment — no recurring billing",
    icon: <Package className="h-4 w-4" />,
    contractType: "prepaid_package",
    cycleDays: 30,
    billingLabel: "One-time payment (no auto-renewal)",
  },
};

function getLeadTypeDefaults(preset: PackagePreset, leadTypes: string[]): { leads: number; price: number } {
  // Use the first selected lead type's defaults, or fallback
  const primaryType = leadTypes[0];
  if (primaryType && LEAD_TYPE_DEFAULTS[preset][primaryType]) {
    return LEAD_TYPE_DEFAULTS[preset][primaryType];
  }
  return PRESET_FALLBACK_DEFAULTS[preset];
}

// --- Proposal Status Badge ---

function ProposalStatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string }> = {
    pending: { label: "Pending", className: "bg-muted text-muted-foreground" },
    awaiting_payment: { label: "Awaiting Payment", className: "bg-yellow-500/10 text-yellow-700 border-yellow-300" },
    paid: { label: "Paid", className: "bg-blue-500/10 text-blue-600 border-blue-200" },
    contract_created: { label: "Contract Created", className: "bg-green-500/10 text-green-600 border-green-200" },
    signup_complete: { label: "Signup Complete", className: "bg-emerald-600/10 text-emerald-700 border-emerald-300" },
    stripe_error: { label: "Stripe Error", className: "bg-destructive/10 text-destructive border-destructive/20" },
  };
  const { label, className } = config[status] ?? { label: status, className: "bg-muted text-muted-foreground" };
  return <Badge variant="outline" className={className}>{label}</Badge>;
}

// --- Package Preset Selector ---

function PresetSelector({
  selected,
  onSelect,
}: {
  selected: PackagePreset;
  onSelect: (preset: PackagePreset) => void;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {(Object.entries(PRESETS) as [PackagePreset, PresetConfig][]).map(([key, preset]) => {
        const isSelected = selected === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(key)}
            className={`relative rounded-lg border-2 p-4 text-left transition-all ${
              isSelected
                ? "border-primary bg-primary/5 shadow-sm"
                : "border-border hover:border-muted-foreground/30 hover:bg-muted/30"
            }`}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <span className={isSelected ? "text-primary" : "text-muted-foreground"}>
                {preset.icon}
              </span>
              <span className="font-medium text-sm">{preset.label}</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {preset.description}
            </p>
            {key === "biweekly" && (
              <Badge variant="secondary" className="absolute top-2 right-2 text-[10px] px-1.5 py-0">
                Upsell funnel
              </Badge>
            )}
          </button>
        );
      })}
    </div>
  );
}

// --- Payment Link Generator ---

export function PaymentLinkGenerator() {
  const [plAgentName, setPlAgentName] = useState("");
  const [plAgentEmail, setPlAgentEmail] = useState("");
  const [plVertical, setPlVertical] = useState<string>("life_insurance");
  const [plLeadTypes, setPlLeadTypes] = useState<string[]>(["life_iul"]);
  const [plTotalLeads, setPlTotalLeads] = useState(40);
  const [plPricePerLead, setPlPricePerLead] = useState(55);
  const [plCycleDays, setPlCycleDays] = useState(30);
  const [plNotes, setPlNotes] = useState("");
  const [plGenerating, setPlGenerating] = useState(false);
  const [plCheckoutUrl, setPlCheckoutUrl] = useState<string | null>(null);
  const [plError, setPlError] = useState<string | null>(null);

  const [selectedPreset, setSelectedPreset] = useState<PackagePreset>("monthly");

  const activePreset = PRESETS[selectedPreset];

  // Apply lead-type-aware defaults when preset or lead types change
  const applyDefaults = (preset: PackagePreset, leadTypes: string[]) => {
    const defaults = getLeadTypeDefaults(preset, leadTypes);
    setPlTotalLeads(defaults.leads);
    setPlPricePerLead(defaults.price);
    setPlCycleDays(PRESETS[preset].cycleDays);
  };

  const handlePresetChange = (preset: PackagePreset) => {
    setSelectedPreset(preset);
    applyDefaults(preset, plLeadTypes);
    setPlCheckoutUrl(null);
    setPlError(null);
  };

  const handleLeadTypeToggle = (leadTypeId: string, checked: boolean) => {
    const newLeadTypes = checked
      ? [...plLeadTypes, leadTypeId]
      : plLeadTypes.filter((id) => id !== leadTypeId);
    setPlLeadTypes(newLeadTypes);
    // Update defaults based on new primary lead type
    if (checked && newLeadTypes.length === 1) {
      applyDefaults(selectedPreset, newLeadTypes);
    }
  };

  const totalAmount = plTotalLeads * plPricePerLead;

  const billingDescription = useMemo(() => {
    if (activePreset.contractType === "prepaid_package") {
      return `One-time payment of $${totalAmount.toFixed(2)} for ${plTotalLeads} leads over ${plCycleDays} days`;
    }
    return `$${totalAmount.toFixed(2)} every ${plCycleDays} days for ${plTotalLeads} leads (auto-renews)`;
  }, [activePreset.contractType, totalAmount, plTotalLeads, plCycleDays]);

  const handleGenerate = async () => {
    if (!plAgentName || !plAgentEmail) {
      setPlError("Agent name and email are required.");
      return;
    }
    if (plLeadTypes.length === 0) {
      setPlError("Select at least one lead type.");
      return;
    }
    setPlGenerating(true);
    setPlError(null);
    setPlCheckoutUrl(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setPlError("Not authenticated.");
        setPlGenerating(false);
        return;
      }

      const response = await supabase.functions.invoke("create-checkout-session", {
        body: {
          agentName: plAgentName,
          agentEmail: plAgentEmail,
          vertical: plVertical,
          leadTypes: plLeadTypes,
          totalLeads: plTotalLeads,
          pricePerLead: plPricePerLead,
          contractType: activePreset.contractType,
          cycleDays: plCycleDays,
          notes: plNotes,
        },
      });

      if (response.error) {
        setPlError(response.error.message || "Failed to generate link.");
      } else if (response.data?.checkoutUrl) {
        setPlCheckoutUrl(response.data.checkoutUrl);
      } else {
        setPlError("No checkout URL returned. Check Stripe configuration.");
      }
    } catch (err: any) {
      setPlError(err.message || "Unexpected error.");
    }

    setPlGenerating(false);
  };

  const copyToClipboard = () => {
    if (plCheckoutUrl) {
      navigator.clipboard.writeText(plCheckoutUrl);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-accent" />
          <CardTitle>Generate Payment Link</CardTitle>
        </div>
        <CardDescription>
          Create a Stripe payment link to send to a new or existing agent.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Section 1: Package Type */}
        <div className="space-y-3">
          <Label className="text-sm font-semibold text-foreground">Package Type</Label>
          <PresetSelector selected={selectedPreset} onSelect={handlePresetChange} />
        </div>

        <Separator />

        {/* Section 2: Agent Info */}
        <div className="space-y-3">
          <Label className="text-sm font-semibold text-foreground">Agent Information</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Name</Label>
              <Input
                placeholder="John Smith"
                value={plAgentName}
                onChange={(e) => setPlAgentName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Email</Label>
              <Input
                type="email"
                placeholder="agent@example.com"
                value={plAgentEmail}
                onChange={(e) => setPlAgentEmail(e.target.value)}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Section 3: Product Selection */}
        <div className="space-y-3">
          <Label className="text-sm font-semibold text-foreground">Lead Configuration</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Vertical</Label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={plVertical}
                onChange={(e) => {
                  setPlVertical(e.target.value);
                  const firstType = LEAD_TYPES_BY_VERTICAL[e.target.value]?.[0]?.id;
                  const newTypes = firstType ? [firstType] : [];
                  setPlLeadTypes(newTypes);
                  applyDefaults(selectedPreset, newTypes);
                }}
              >
                <option value="life_insurance">Life Insurance</option>
                <option value="mortgage">Mortgage</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Lead Types</Label>
              <div className="space-y-2 rounded-md border p-3">
                {(LEAD_TYPES_BY_VERTICAL[plVertical] || []).map((lt) => (
                  <div key={lt.id} className="flex items-center justify-between">
                    <span className="text-sm">{lt.name}</span>
                    <Switch
                      checked={plLeadTypes.includes(lt.id)}
                      onCheckedChange={(checked) => handleLeadTypeToggle(lt.id, checked)}
                    />
                  </div>
                ))}
                {plLeadTypes.length === 0 && (
                  <p className="text-xs text-destructive">Select at least one lead type</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Section 4: Pricing & Billing */}
        <div className="space-y-3">
          <Label className="text-sm font-semibold text-foreground">Pricing & Billing</Label>
          <div className={`grid grid-cols-2 ${selectedPreset === "prepaid" ? "md:grid-cols-4" : "md:grid-cols-3"} gap-4`}>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Leads</Label>
              <Input
                type="number"
                min={1}
                value={plTotalLeads}
                onChange={(e) => setPlTotalLeads(Number(e.target.value))}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Price per Lead ($)</Label>
              <Input
                type="number"
                min={0.5}
                step={0.5}
                value={plPricePerLead}
                onChange={(e) => setPlPricePerLead(Number(e.target.value))}
              />
            </div>
            {selectedPreset === "prepaid" && (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Duration (days)</Label>
                <Input
                  type="number"
                  min={7}
                  value={plCycleDays}
                  onChange={(e) => setPlCycleDays(Number(e.target.value))}
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Total</Label>
              <div className="h-9 flex items-center px-3 rounded-md border bg-muted/50 text-sm font-semibold">
                ${totalAmount.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Billing summary */}
          <div className="rounded-md bg-muted/40 border px-3 py-2">
            <p className="text-xs text-muted-foreground">{billingDescription}</p>
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Notes (optional)</Label>
          <Input
            placeholder="Special terms, discounts, etc."
            value={plNotes}
            onChange={(e) => setPlNotes(e.target.value)}
          />
        </div>

        {plError && <p className="text-sm text-destructive">{plError}</p>}

        {plCheckoutUrl && (
          <div className="rounded-lg border border-green-200 bg-green-50/50 p-4 space-y-3">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm font-medium">Payment link generated!</span>
            </div>
            <div className="flex gap-2">
              <Input
                readOnly
                value={plCheckoutUrl}
                className="text-xs"
              />
              <Button variant="outline" size="sm" onClick={copyToClipboard}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Send this link to the agent. After payment, they'll be redirected to create their account.
            </p>
          </div>
        )}

        <Button onClick={handleGenerate} disabled={plGenerating} className="w-full">
          {plGenerating ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <LinkIcon className="h-4 w-4 mr-2" />
          )}
          Generate {activePreset.label} Link
        </Button>
      </CardContent>
    </Card>
  );
}

// --- Payment Links Tracker ---

export function PaymentLinksTracker() {
  const [proposals, setProposals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProposals = async () => {
    setLoading(true);
    const { data } = await (supabase
      .from("contract_proposals" as any)
      .select("*")
      .order("created_at", { ascending: false })
      .limit(100) as any);
    setProposals(data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    fetchProposals();
  }, []);

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "\u2014";
    return new Date(dateStr).toLocaleString();
  };

  const getPackageLabel = (proposal: any): string => {
    if (proposal.contract_type === "prepaid_package") return "Prepaid";
    if (proposal.cycle_days === 14) return "Biweekly";
    return "Monthly";
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5 text-accent" />
            <CardTitle>Payment Links</CardTitle>
          </div>
          <Button variant="outline" size="sm" onClick={fetchProposals} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
        <CardDescription>Track all generated payment links and their lifecycle status.</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : proposals.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No payment links generated yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">Agent</th>
                  <th className="pb-2 pr-4 font-medium">Package</th>
                  <th className="pb-2 pr-4 font-medium">Status</th>
                  <th className="pb-2 pr-4 font-medium">Created</th>
                  <th className="pb-2 pr-4 font-medium">Paid At</th>
                  <th className="pb-2 pr-4 font-medium">Contract</th>
                  <th className="pb-2 font-medium">Link</th>
                </tr>
              </thead>
              <tbody>
                {proposals.map((p: any) => (
                  <tr key={p.id} className="border-b last:border-0">
                    <td className="py-3 pr-4">
                      <div className="font-medium">{p.agent_name || "\u2014"}</div>
                      <div className="text-xs text-muted-foreground">{p.agent_email || "\u2014"}</div>
                    </td>
                    <td className="py-3 pr-4">
                      <Badge variant="outline" className="text-xs">
                        {getPackageLabel(p)}
                      </Badge>
                    </td>
                    <td className="py-3 pr-4">
                      <ProposalStatusBadge status={p.status} />
                    </td>
                    <td className="py-3 pr-4 text-muted-foreground text-xs">
                      {formatDate(p.created_at)}
                    </td>
                    <td className="py-3 pr-4 text-muted-foreground text-xs">
                      {formatDate(p.paid_at)}
                    </td>
                    <td className="py-3 pr-4">
                      {p.contract_id ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-mono">{p.contract_id.slice(0, 20)}&hellip;</span>
                          {p.contract_id.startsWith("recovered_") && (
                            <Badge variant="outline" className="bg-orange-500/10 text-orange-600 border-orange-300 text-[10px]">
                              Recovery
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">&mdash;</span>
                      )}
                    </td>
                    <td className="py-3">
                      {p.stripe_checkout_url ? (
                        <Button variant="ghost" size="sm" onClick={() => copyUrl(p.stripe_checkout_url)}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">&mdash;</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
